import loadContextData from "../../utils/loadContextData";
import loadPreactModule from "../../utils/loadPreactModule";
import renderPreactWithProps from "../../utils/renderPreactWithProps";
import callIfArgumentsPass from "../../utils/callIfArgumentsPass";
import curry from "../../lib/core/functional/curry";
import thunk from "../../lib/core/functional/thunk";
import noop from "../../lib/core/functional/noop";
import * as transport from "../../transport/like";

function didUserInfoChanged(prevErr, prevUserInfo, err, userInfo) {
    if (prevErr != err) {
        return true;
    }

    return userInfo.profileSite.id !== prevUserInfo.profileSite.id;
}

export const renderLikeButton = curry(
    (renderModule, context, element, totalLikes, like, unlike, getLikes, err, userInfo) => {
        if (err) {
            renderModule({ totalLikes, isLoggedIn: false, isLiked: false, toggleLike: noop });
            return;
        }

        const userId = `${userInfo.profileSite.id}`;
        const isLoggedIn = true;
        let inProgress = false;

        function resetProgress() {
            inProgress = false;
        }

        function makePropsAndRender(isLiked) {
            const props = {
                isLoggedIn,
                isLiked,
                totalLikes,
                toggleLike: toggleLike(isLiked),
            };

            renderModule(props);
        }

        const toggleLike = curry((liked, event) => {
            if (inProgress) {
                return true;
            }
            inProgress = true;
            const fun = liked ? unlike : like;
            fun(userId)
                .then(onTransportCallSuccess(liked))
                .catch(console.error)
                .finally(resetProgress);

            event.preventDefault();
            return false;
        });

        const onTransportCallSuccess = thunk((liked) => {
            makePropsAndRender(!liked);
        });

        function onLikesGotten(res) {
            makePropsAndRender(userId in res);
        }

        function onIntersect() {
            getLikes().then(onLikesGotten).catch(console.error);
        }

        context.intersectionObserverProvider.addTask({
            onIntersect,
            rawNodes: [element],
            threshold: 200,
            oneTime: true,
        });

        makePropsAndRender(false);
    }
);

const onModule = curry(function onModule(context, element, [renderer, Module]) {
    const parentPost = element.closest("article[elid]");
    const postId = parentPost.getAttribute("elid");
    const contextData = loadContextData(`.post-context-${postId}`, parentPost);
    const authorId = contextData.post.authors[0];
    const likes = "postLikes" in element.dataset ? parseInt(element.dataset.postLikes, 10) : 0;

    const like = curry(transport.create)(authorId, postId);
    const unlike = thunk(transport.remove)(authorId, postId);
    const getLikes = thunk(transport.list)(postId);
    
    context.listenForUserInfo(
        callIfArgumentsPass(
            didUserInfoChanged,
            renderLikeButton(
                renderPreactWithProps(renderer, Module, element),
                context,
                element,
                likes,
                like,
                unlike,
                getLikes
            )
        )
    );
});

const internalLoader = () => import("./module").then((module) => module.default);

export default function renderer(context, element) {
    loadPreactModule("LikeButton", context, {}, internalLoader).then(onModule(context, element));
}
